import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {Field, ProductMutation} from '../../interfaces/field';
import {UntypedFormGroup} from '@angular/forms';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {StateService} from '../../services/state.service';
import {CurrencyPipe} from '@angular/common';
import {UnicornPipe} from '../../pipes/unicorn.pipe';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-checkbox-field',
    templateUrl: './checkbox-field.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./checkbox-field.component.scss'],
    providers: [CurrencyPipe, UnicornPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CheckboxFieldComponent implements OnInit, OnDestroy {
  @Input() config: Field;
  @Input() control: UntypedFormGroup;
  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  hasErrors = false;
  placeholder = '';
  description = '';
  productMutation: ProductMutation;
  returnTripSub: Subscription;

  constructor(private translate: TranslateService,
              private state: StateService,
              private currencyPipe: CurrencyPipe,
              private unicornPipe: UnicornPipe) {
  }

  ngOnInit(): void {
    this.returnTripSub = this.control.controls['returnTrip'].valueChanges.subscribe(this.onReturnTripChanges.bind(this));
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.defineDescription();
    });

    this.defineDescription();
  }


  defineDescription(): void {
    if (!this.config.label) {
      const self = this;
      setTimeout(function () {
        self.defineDescription();
      }, 100);
      return;
    }

    if (this.config.product) {
      const amount = this.config.product.amount;
      if (this.config.product && this.config.product.type === 'airportDepartureJob') {
        if (this.state.airportDestination && !this.control.controls['returnTrip'].value) {
          delete this.config.product;
        }
      } else if (this.config.product && this.config.product.type === 'airportDestinationJob') {
        if (this.state.airportDeparture && !this.control.controls['returnTrip'].value) {
          delete this.config.product;
        }
      }
    }

    const tempUrls = this.config.documentUrls ? this.config.documentUrls : [];
    const translatedUrls = [];
    Object.keys(tempUrls).forEach(k => {
      translatedUrls.push(tempUrls[k]);
    });
    this.translate.get(this.config.placeholder).subscribe(placeholder => {
      this.placeholder = placeholder;

      this.placeholder = this.unicornPipe.transform(this.placeholder, translatedUrls);

      if (this.config.product) {
        this.productMutation = this.config.product;
        const isReturnTrip = this.control.controls['returnTrip'].value;
        // v2-authored fields (api.taxiid.nl) carry returnSurchargeMode/payoutToDriver and
        // use final per-leg amounts. For a return trip the passenger pays amount +
        // returnAmount (both/split/outbound already applied), so show that combined total.
        // Legacy fields lack those keys and store the full return-trip surcharge in
        // returnAmount, so keep showing that as-is.
        const isPerLeg = this.productMutation.returnSurchargeMode !== undefined ||
          this.productMutation.payoutToDriver !== undefined;
        let amount;
        if (isPerLeg) {
          amount = isReturnTrip ?
            (this.productMutation.amount + (this.productMutation.returnAmount || 0)) :
            this.productMutation.amount;
        } else {
          amount = (isReturnTrip && this.productMutation.returnAmount ?
            this.productMutation.returnAmount :
            this.productMutation.amount);
        }
        const amountFormatted: any = this.currencyPipe.transform((amount / 100), this.state.currency, 'symbol', '1.2-2');
        this.description = `${this.placeholder} (${amountFormatted})`;
      } else {
        this.description = this.placeholder;
      }
    });
  }

  onReturnTripChanges(value: any): void {
    this.defineDescription();
  }

  toggle(event: any) {
    const patch = {};
    patch[this.config.property] = event.target.checked;
    if (event.target.checked) {
      this.validityChanged(false);
      this.state.orderPosted = false;
    } else {
      this.validityChanged(true);
    }
    this.control.patchValue(patch);
  }

  validityChanged(valid: boolean) {
    this.hasErrors = valid;
  }

  ngOnDestroy(): void {
    if (this.returnTripSub) {
      this.returnTripSub.unsubscribe();
    }
  }

  toggleHelpModal(helpTitleTag: string, helpTextTag: string, type?: string, meta?: any) {
    this.openHelpModal.emit({
      helpTitleTag,
      helpTextTag,
      type,
      meta,
    });
  }

  protected readonly faInfoCircle = 'info-circle';
}
